import React, { useState, useEffect, useRef } from 'react';
import { Users, Orbit, GitBranch } from 'lucide-react';
import VideoModal from './VideoModal';

/* TEMPORARY placeholder clip — swap to the user's uploaded gameplay video. */
const GAMEPLAY_VIDEO_SRC = '/gameplay-video.mp4';

const NAV_OFFSET = 72;
const scrollToId = (id) => {
  if (id === 'home') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  window.scrollTo({ top: y, behavior: 'smooth' });
};

/* ============================================================
   LAYER 1 — Bright cinematic photographic background
   ============================================================ */
const HeroBackground = ({ parallaxX = 0, parallaxY = 0 }) => (
  <div
    className="absolute inset-0 hero-fade-in"
    data-testid="hero-bg-layer"
    style={{
      backgroundImage: 'url("/backgrounds/hero-main-background-v2.png")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      transform: `translate3d(${parallaxX * -0.3}px, ${parallaxY * -0.15}px, 0) scale(1.04)`,
      transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
      willChange: 'transform',
    }}
  />
);

/* ============================================================
   LAYER 2 — HERO TITLE BLOCK (dark ink on icy background)
   ============================================================ */
const INK = 'var(--text-primary)';
const RED = '#FF8618';

const HeroTitle = () => (
  <div className="max-w-2xl" data-testid="hero-title-block">
    <p
      className="font-black uppercase leading-none tracking-[0.06em] mb-3 hero-fade-in"
      style={{
        fontFamily: '"Black Ops One", "Bebas Neue", sans-serif',
        fontSize: 'clamp(1.4rem, 2.4vw, 2rem)',
        color: INK,
        textShadow: '0 2px 10px rgba(0,0,0,0.6)',
        animationDelay: '0ms',
      }}
      data-testid="hero-eyebrow"
    >
      A NEW ERA OF
    </p>

    <h1 className="hero-distressed-ink hero-fade-in" style={{ fontFamily: '"Black Ops One", sans-serif', fontSize: 'clamp(3.5rem, 8vw, 7rem)', animationDelay: '120ms' }} data-testid="hero-title">
      SCI-FI
    </h1>

    <h2
      className="hero-fade-in"
      style={{
        fontFamily: '"Black Ops One", "Russo One", sans-serif',
        fontSize: 'clamp(2.4rem, 5.6vw, 4.6rem)',
        color: RED,
        textShadow: '0 0 22px rgba(255, 134, 24, 0.45), 0 2px 10px rgba(0,0,0,0.6)',
        fontWeight: 'normal', letterSpacing: '0.01em', lineHeight: '0.92', marginTop: '4px', animationDelay: '220ms',
      }}
    >
      MULTIPLAYER
    </h2>

    <h2 className="hero-distressed-ink hero-fade-in" style={{ fontFamily: '"Black Ops One", sans-serif', fontSize: 'clamp(2.6rem, 6.2vw, 5rem)', marginTop: '6px', animationDelay: '320ms' }}>
      COMBAT
    </h2>

    <div className="mt-6 flex items-center gap-3 hero-fade-in" style={{ animationDelay: '440ms' }}>
      <span className="block h-[3px] w-12" style={{ background: RED, boxShadow: '0 0 12px rgba(255,134,24,0.5)' }} />
      <p
        className="text-base md:text-lg tracking-wide"
        style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.16em', textTransform: 'uppercase', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}
      >
        Assemble your faction.
      </p>
    </div>
  </div>
);

/* ============================================================
   LAYER 3 — DEPLOY NOW BUTTON (red brand CTA)
   ============================================================ */
const DeployButton = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="deploy-now-btn"
    className="deploy-btn group relative mt-8 inline-flex items-center justify-between gap-4 pl-6 pr-5 py-4 overflow-hidden hero-fade-in"
    style={{
      minWidth: '320px', animationDelay: '560ms',
      background: 'linear-gradient(180deg, #FF8618 0%, #F07310 100%)',
      border: '1px solid #C6530D',
      boxShadow: '0 8px 24px rgba(255,134,24,0.40), 0 0 0 1px rgba(255,255,255,0.35), inset 0 1px 0 rgba(255,255,255,0.35)',
      borderRadius: '4px',
      clipPath: 'none',
    }}
  >
    <span className="relative z-10 flex items-center">
      <img
        src="/galwar-logo.png"
        alt="GAL WAR CC"
        data-testid="deploy-logo"
        style={{ width: '34px', height: '34px', objectFit: 'contain', imageRendering: '-webkit-optimize-contrast', filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.45))' }}
      />
    </span>
    <span className="relative z-10 text-lg font-black tracking-[0.32em] uppercase" style={{ fontFamily: '"Rajdhani", "Orbitron", sans-serif', fontWeight: 700, color: '#FFFFFF', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}>
      DEPLOY NOW
    </span>
    <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0 relative z-10 deploy-btn-arrow" fill="none" style={{ transition: 'transform 240ms cubic-bezier(0.22,1,0.36,1)', color: '#FFFFFF' }}>
      <path d="M 4 12 L 18 12 M 12 6 L 18 12 L 12 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

/* ============================================================
   LAYER 3b — PROTOTYPE PLAY (circular, dark ink on icy bg)
   ============================================================ */
const PrototypePlay = ({ onPlay }) => (
  <div
    className="prototype-panel flex flex-col items-center text-center hero-fade-in"
    style={{ animationDelay: '720ms' }}
    data-testid="prototype-play-block"
  >
    <span className="prototype-eyebrow" data-testid="prototype-eyebrow">Prototype Footage</span>

    <button
      onClick={onPlay}
      data-testid="watch-trailer-btn"
      className="prototype-play-ink group relative w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mt-4"
      style={{ border: '2px solid rgba(255,255,255,0.55)', background: 'rgba(10,12,14,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', boxShadow: '0 10px 30px rgba(0,0,0,0.6)', transition: 'box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease' }}
    >
      <span className="absolute inset-0 rounded-full prototype-play-ring" style={{ border: '1px solid rgba(255,134,24,0.5)' }} />
      <svg width="30" height="30" viewBox="0 0 30 30" fill="#FFFFFF">
        <path d="M11 8 L11 22 L23 15 Z" />
      </svg>
    </button>

    <h3 className="prototype-heading mt-5" data-testid="prototype-heading">
      Watch The Prototype Gameplay
    </h3>

    <button
      onClick={onPlay}
      data-testid="watch-more-btn"
      className="watch-more-btn group mt-5 inline-flex items-center gap-2.5"
    >
      <span className="watch-more-label">Watch More Videos</span>
      <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="watch-more-arrow">
        <path d="M3 7 L11 7 M7 3 L11 7 L7 11" />
      </svg>
    </button>
  </div>
);

/* ============================================================
   LAYER 4 — BOTTOM GLASS STAT BAR (light frosted glass, 3 stats)
   ============================================================ */
const NAV_ITEMS = [
  { Icon: Users, label: '5 RACES', target: 'races', testid: 'hero-stat-races' },
  { Icon: Orbit, label: '5 PLANETS', target: 'map', testid: 'hero-stat-planets' },
  { Icon: GitBranch, label: '\u221E STRATEGIES', target: 'gameplay', testid: 'hero-stat-strategies' },
];

const GlassNavBar = ({ onNavigate }) => (
  <div className="relative z-10 px-4 sm:px-6 lg:px-10 pb-6 hero-fade-in" style={{ animationDelay: '680ms' }}>
    <div className="max-w-[560px] mx-auto">
      <div
        className="grid grid-cols-3 max-[380px]:grid-cols-1 rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(14, 19, 24, 0.72)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(20px) saturate(140%)',
          WebkitBackdropFilter: 'blur(20px) saturate(140%)',
          boxShadow: '0 18px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
        data-testid="hero-glass-bar"
      >
        {NAV_ITEMS.map((it, i) => (
          <button
            key={it.label}
            onClick={() => onNavigate(it.target)}
            data-testid={it.testid}
            className="glass-nav-item group relative flex flex-col items-center justify-center gap-2.5 px-3 py-5"
            style={{ borderRight: i < NAV_ITEMS.length - 1 ? '1px solid rgba(255,255,255,0.11)' : 'none' }}
          >
            <it.Icon
              className="w-10 h-10 text-white/85 transition-all duration-300 group-hover:text-[#FF8618] group-hover:-translate-y-0.5"
              strokeWidth={1.6}
            />
            <span
              className="text-center uppercase leading-tight"
              style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, fontSize: '11px', letterSpacing: '0.12em', color: 'var(--text-muted)' }}
            >
              {it.label}
            </span>
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] w-0 group-hover:w-2/3 transition-all duration-300" style={{ background: RED, boxShadow: '0 0 5px rgba(255,134,24,0.45)' }} />
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   ROOT — HERO COMPOSITION
   ============================================================ */
const Hero = () => {
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [videoOpen, setVideoOpen] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      setParallax({ x: Math.max(-20, Math.min(20, dx * 40)), y: Math.max(-15, Math.min(15, dy * 30)) });
    };
    const node = heroRef.current;
    node?.addEventListener('mousemove', handler);
    return () => node?.removeEventListener('mousemove', handler);
  }, []);

  return (
    <section id="home" ref={heroRef} data-testid="hero-section" className="relative min-h-[86vh] flex flex-col overflow-hidden" style={{ background: 'rgba(10, 14, 19, 0.50)' }}>
      <HeroBackground parallaxX={parallax.x} parallaxY={parallax.y} />

      {/* Cinematic dark scrims for legibility over the key-art */}
      <div className="absolute inset-0 z-[2] pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(10,13,16,0.76) 0%, rgba(10,13,16,0.46) 34%, rgba(10,13,16,0.0) 62%)' }} />
      <div className="absolute inset-x-0 top-0 h-28 z-[2] pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(10,13,16,0.71) 0%, transparent 100%)' }} />
      <div className="absolute inset-x-0 bottom-0 h-64 z-[2] pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(10,13,16,0.78) 88%)' }} />

      {/* Spacer for fixed nav */}
      <div className="h-20 z-10" />

      {/* Title (left) + Prototype play (right) */}
      <div className="relative z-10 flex-1 flex items-center px-6 sm:px-10 lg:px-16">
        <div className="w-full grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <HeroTitle />
            <DeployButton onClick={() => scrollToId('gameplay')} />
          </div>
          <div className="hidden lg:block pr-6" style={{ alignSelf: 'center' }}>
            <PrototypePlay onPlay={() => setVideoOpen(true)} />
          </div>
        </div>
      </div>

      {/* Bottom glass nav bar */}
      <GlassNavBar onNavigate={scrollToId} />

      {/* Prototype gameplay video popup */}
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} src={GAMEPLAY_VIDEO_SRC} title="Prototype Gameplay" />
    </section>
  );
};

export default Hero;
